import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import classList from "flarum/common/utils/classList";
import chatState from "../state/chat";
import ChatSidebar from "./ChatSidebar";
import ErrorBoundary from "./ErrorBoundary";
import ChannelView from "./ChannelView";
import PinnedPanel from "./PinnedPanel";
import ThreadPanel from "./ThreadPanel";
import { chatTitle, chatIcon } from "../utils/branding";
import { isNarrowViewport } from "../utils/surface";

/**
 * The floating chat panel, pinned bottom-right over whatever page is open.
 *
 * Mounted once at the app root rather than per-page, so navigating the forum does
 * not tear down an open conversation — which is the entire point of a drawer.
 *
 * Below the mobile breakpoint it is not a panel at all. There is no room for one
 * over the page: it filled the viewport, covering the header, the phone toolbar
 * and its drawer toggle, so the forum underneath was unreachable until the chat
 * was closed. At that width the open drawer renders as a floating button
 * instead, and tapping it hands the conversation to the full-screen page — the
 * surface a phone has for the chat anyway (see `shouldUseChatDrawer`).
 */
export default class ChatDrawer extends Component {
  constructor() {
    super(...arguments);
    /** Last known answer to `isNarrowViewport()`, so resize only redraws on a change. */
    _defineProperty(this, "narrow", isNarrowViewport());
    _defineProperty(this, "onResize", () => {
      const narrow = isNarrowViewport();
      if (narrow === this.narrow) return;
      this.narrow = narrow;
      m.redraw();
    });
  }
  /**
   * Nothing else watches the viewport, and the panel-versus-button choice is made
   * in `view()` rather than by a media query, so without this a rotation left the
   * old shape on screen until some other event happened to redraw.
   */
  oncreate(vnode) {
    super.oncreate(vnode);
    window.addEventListener("resize", this.onResize);
  }
  onremove(vnode) {
    super.onremove(vnode);
    window.removeEventListener("resize", this.onResize);
  }
  view() {
    if (!chatState.drawerOpen) return null;

    // Read here as well as in the resize handler: the drawer can be opened at any
    // width (a restored session, an invite followed on a phone) without a resize
    // ever firing.
    this.narrow = isNarrowViewport();
    if (this.narrow) return this.floatingButton();
    const channel = chatState.channel(chatState.activeChannelId);
    const title = chatTitle();
    const icon = chatIcon();
    const unread = chatState.unreadSummary();
    return m("div", {
      className: classList("ChatDrawer", {
        "ChatDrawer--collapsed": chatState.drawerCollapsed
      }),
      role: "complementary",
      "aria-label": title
    }, m("div", {
      className: "ChatDrawer-header",
      onclick: () => this.toggleCollapsed()
    }, channel ? m(Button, {
      className: "Button Button--icon Button--flat",
      icon: "fas fa-chevron-left",
      onclick: e => {
        e.stopPropagation();
        chatState.setActiveChannel(null);
      }
    }) : icon ? m("i", {
      className: icon,
      "aria-hidden": "true"
    }) : null, m("span", {
      className: "ChatDrawer-title"
    }, channel ? channel.displayName() : title), chatState.drawerCollapsed && unread.mentions > 0 ? m("span", {
      className: "ChatDrawer-badge ChatDrawer-badge--mention",
      title: app.translator.trans("ramon-chat.forum.nav.unread_mentions", {
        count: unread.mentions
      }, true)
    }, unread.mentions > 99 ? "99+" : unread.mentions) : chatState.drawerCollapsed && unread.messages > 0 ? m("span", {
      className: "ChatDrawer-badge",
      title: app.translator.trans("ramon-chat.forum.nav.unread_messages", {
        count: unread.messages
      }, true)
    }, unread.messages > 99 ? "99+" : unread.messages) : null, m("div", {
      className: "ChatDrawer-actions"
    }, m(Button, {
      className: "Button Button--icon Button--flat",
      icon: "fas fa-up-right-and-down-left-from-center",
      title: app.translator.trans("ramon-chat.forum.drawer.full_screen", {}, true),
      onclick: e => {
        e.stopPropagation();
        this.goFullScreen();
      }
    }), m(Button, {
      className: "Button Button--icon Button--flat",
      icon: chatState.drawerCollapsed ? "fas fa-chevron-up" : "fas fa-chevron-down",
      title: app.translator.trans(chatState.drawerCollapsed ? "ramon-chat.forum.drawer.expand" : "ramon-chat.forum.drawer.collapse", {}, true),
      onclick: e => {
        e.stopPropagation();
        this.toggleCollapsed();
      }
    }), m(Button, {
      className: "Button Button--icon Button--flat",
      icon: "fas fa-times",
      title: app.translator.trans("ramon-chat.forum.drawer.close", {}, true),
      onclick: e => {
        e.stopPropagation();
        this.close();
      }
    }))), chatState.drawerCollapsed ? null : m("div", {
      className: "ChatDrawer-body"
    }, m(ErrorBoundary, {
      area: "drawer"
    }, this.body(channel))));
  }

  /**
   * The drawer, at a width where a panel over the page would cover the page.
   *
   * A bubble in the corner, over the forum rather than instead of it: the header,
   * the phone toolbar and its drawer toggle all stay reachable, which is the whole
   * complaint against the full-bleed panel this replaces. Tapping it opens the
   * full-screen chat, so the button is a way *in* rather than a second chat.
   *
   * The dismiss cross is small but present, and deliberately so: `drawerOpen`
   * survives reloads, so without it a drawer opened once on a desktop would
   * follow the reader onto their phone with no way to send it away — the header
   * button only opens the chat.
   */
  floatingButton() {
    var _chatIcon;
    const label = chatTitle();
    const unread = chatState.unreadSummary();
    const count = unread.mentions > 0 ? unread.mentions : unread.messages;

    // A bubble with neither icon nor text would be an unlabelled circle, so the
    // admin's "no icon" setting cannot apply here the way it does to the header
    // button, which sits beside its own label in the drawer.
    const icon = (_chatIcon = chatIcon()) != null ? _chatIcon : "fas fa-comments";
    const close = app.translator.trans("ramon-chat.forum.drawer.close", {}, true);
    return m("div", {
      className: "ChatFab"
    }, m("button", {
      className: "ChatFab-button",
      type: "button",
      "aria-label": label,
      title: label,
      onclick: () => this.goFullScreen()
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), count > 0 ? m("span", {
      className: classList("ChatFab-badge", {
        "ChatFab-badge--mention": unread.mentions > 0
      })
    }, count > 99 ? "99+" : count) : null), m(Button, {
      className: "Button Button--icon ChatFab-dismiss",
      icon: "fas fa-times",
      title: close,
      "aria-label": close,
      onclick: () => this.close()
    }));
  }

  /**
   * The drawer's contents: the conversation, or the channel list, plus the pinned
   * panel over the top when it is open.
   *
   * Built as an array rather than a JSX fragment with a conditional slot. Mithril
   * decides a fragment is keyed from its first child and then demands every other
   * child be keyed too — and it counts `null` as unkeyed. `[<ChannelView key=…/>,
   * null]`, which is what a closed pinned panel produced, therefore threw "In
   * fragments, vnodes must either all have keys or none have keys" on every redraw.
   * The drawer is mounted at the app root, so that fired on every page of the forum,
   * not only inside the chat.
   */
  body(channel) {
    const panes = [channel ? m(ChannelView, {
      key: "channel-".concat(channel.id()),
      channel: channel,
      state: chatState,
      embedded: true
    }) : m(ChatSidebar, {
      key: "sidebar",
      state: chatState,
      onSelect: c => this.select(c)
    })];

    // A thread takes the same overlay slot as the pinned list. Only one of the two
    // can be open: opening a thread is what the reply indicator does, and the
    // pinned toggle clears the thread, so they cannot both claim the space.
    if (channel && chatState.activeThreadId !== null) {
      panes.push(m(ThreadPanel, {
        key: "thread-".concat(chatState.activeThreadId),
        channel: channel,
        threadId: chatState.activeThreadId,
        state: chatState,
        onClose: () => {
          chatState.closeThread();
          m.redraw();
        }
      }));
      return panes;
    }

    // The drawer has no room for a side panel, so the pinned list covers the
    // conversation instead — the arrangement the thread panel uses on a phone.
    if (channel && chatState.showPinned) {
      panes.push(m(PinnedPanel, {
        key: "pinned-".concat(channel.id()),
        channel: channel,
        state: chatState,
        embedded: true,
        onClose: () => {
          chatState.showPinned = false;
          m.redraw();
        }
      }));
    }
    return panes;
  }
  select(channel) {
    chatState.setActiveChannel(Number(channel.id()));
    m.redraw();
  }
  toggleCollapsed() {
    chatState.setDrawerCollapsed(!chatState.drawerCollapsed);
    m.redraw();
  }

  /**
   * The only way to close the drawer. Navigating the forum, reloading the page and
   * collapsing the header all leave it open — dismissal has to be deliberate.
   */
  close() {
    chatState.setDrawerOpen(false);
    m.redraw();
  }

  /**
   * Hands the current channel over to the full-screen page. The drawer closes so
   * the two never render the same conversation at once.
   *
   * Suspended rather than closed: the user did not dismiss the chat, they moved it,
   * so leaving the full-screen page puts the drawer back where it was. See
   * ChatPage.onremove, which is what restores it.
   */
  goFullScreen() {
    const id = chatState.activeChannelId;
    chatState.suspendDrawer();
    m.route.set(id ? app.route("chat.channel", {
      id
    }) : app.route("chat.index"));
  }

  /**
   * Opens the drawer, loading the channel list on first open.
   */
  static async open() {
    chatState.setDrawerOpen(true);
    chatState.setDrawerCollapsed(false);
    if (!chatState.channelsLoaded) {
      // loadDrafts() is a no-op for a guest; see ChatState.
      await Promise.all([chatState.loadChannels(), chatState.loadDrafts()]);
    }
    m.redraw();
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatDrawer', ChatDrawer);